// src/personalHabilitiesTests/PersonalHabilitiesTestResult.js
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const getIntegrityDimensionsForReport = (results, language = 'es') => {
  if (Array.isArray(results?.integrityDimensionScores) && results.integrityDimensionScores.length > 0) {
    return results.integrityDimensionScores;
  }

  const factorScores = results?.factorScores || {};

  if (language === 'en') {
    return [
      {
        key: 'h',
        name: 'Personal responsibility and integrity',
        score: factorScores.h || 0,
        description: 'Assesses how the person acts with transparency, care, and consistency when handling resources and making everyday decisions.'
      },
      {
        key: 'p',
        name: 'Transparency and ethical conduct',
        score: factorScores.p || 0,
        description: 'Analyzes ethical clarity when facing dilemmas or external pressure, and the willingness to maintain fair and transparent practices.'
      },
      {
        key: 'c',
        name: 'Adherence to rules and policies',
        score: factorScores.c || 0,
        description: 'Observes the degree of commitment to rules, procedures, and internal policies, even when there is no direct supervision.'
      },
      {
        key: 'a',
        name: 'Emotional management and decision-making',
        score: factorScores.a || 0,
        description: 'Assesses the ability to regulate impulses, maintain self-control, and act prudently under pressure or temptation.'
      },
      {
        key: 'o',
        name: 'Trust in the integrity of the environment',
        score: factorScores.o || 0,
        description: 'Explores the level of trust the person places in the ethical consistency of the workplace environment and in others’ conduct.'
      }
    ];
  }

  return [
    {
      key: 'h',
      name: 'Responsabilidad e integridad personal',
      score: factorScores.h || 0,
      description: 'Evalúa cómo la persona actúa con transparencia, cuidado y coherencia en el manejo de recursos y en sus decisiones cotidianas.'
    },
    {
      key: 'p',
      name: 'Transparencia y conducta ética',
      score: factorScores.p || 0,
      description: 'Analiza la claridad de criterios éticos frente a dilemas o presiones externas, y la disposición a mantener prácticas justas y transparentes.'
    },
    {
      key: 'c',
      name: 'Adhesión a normas y políticas',
      score: factorScores.c || 0,
      description: 'Observa el grado de compromiso con las reglas, procedimientos y políticas internas, incluso cuando no existe supervisión directa.'
    },
    {
      key: 'a',
      name: 'Gestión emocional y toma de decisiones',
      score: factorScores.a || 0,
      description: 'Evalúa la capacidad de regular impulsos, mantener el autocontrol y actuar con prudencia frente a situaciones de presión o tentación.'
    },
    {
      key: 'o',
      name: 'Confianza en la integridad del entorno',
      score: factorScores.o || 0,
      description: 'Explora el nivel de confianza que la persona deposita en la coherencia ética del entorno laboral y en la conducta de otros.'
    }
  ];
};

const getEmotionalIntelligenceDimensionGuide = (language = 'es') => {
  if (language === 'en') {
    return [
      {
        dimension: 'Self-awareness',
        whatItMeasures: 'Emotion recognition, self-confidence, internal clarity.',
        highIndicators: 'Knows themselves, identifies emotions and their impact.',
        lowIndicators: 'Difficulty recognizing emotional states or mistakes.'
      },
      {
        dimension: 'Self-regulation',
        whatItMeasures: 'Impulse control, calmness under pressure, flexibility.',
        highIndicators: 'Maintains stability and responds with self-control.',
        lowIndicators: 'Responds with impulsiveness, anger, or avoidance.'
      },
      {
        dimension: 'Motivation',
        whatItMeasures: 'Persistence, sense of purpose, energy to achieve goals.',
        highIndicators: 'Perseveres, maintains enthusiasm and resilience.',
        lowIndicators: 'Low consistency, dependence on external recognition.'
      },
      {
        dimension: 'Empathy',
        whatItMeasures: 'Emotional understanding of others, social sensitivity.',
        highIndicators: 'Active listening, tact, genuine understanding.',
        lowIndicators: 'Ignores emotional cues, cold communication.'
      },
      {
        dimension: 'Social skills',
        whatItMeasures: 'Communication, influence, conflict resolution.',
        highIndicators: 'Cooperates, persuades, and maintains good relationships.',
        lowIndicators: 'Avoids difficult conversations or imposes ideas.'
      },
      {
        dimension: 'Stress management',
        whatItMeasures: 'Pressure tolerance, emotional balance.',
        highIndicators: 'Prioritizes, protects energy, asks for help in time.',
        lowIndicators: 'Gets overwhelmed, loses focus, or becomes overloaded.'
      }
    ];
  }

  return [
    {
      dimension: 'Autoconciencia',
      whatItMeasures: 'Reconocimiento de emociones, autoconfianza, claridad interna.',
      highIndicators: 'Se conoce, identifica emociones y su impacto.',
      lowIndicators: 'Dificultad para reconocer estados emocionales o errores.'
    },
    {
      dimension: 'Autorregulación',
      whatItMeasures: 'Control de impulsos, calma ante presión, flexibilidad.',
      highIndicators: 'Mantiene estabilidad y responde con autocontrol.',
      lowIndicators: 'Responde con impulsividad, enojo o evasión.'
    },
    {
      dimension: 'Motivación',
      whatItMeasures: 'Persistencia, sentido de propósito, energía para lograr metas.',
      highIndicators: 'Persevera, mantiene entusiasmo y resiliencia.',
      lowIndicators: 'Baja constancia, dependencia del reconocimiento externo.'
    },
    {
      dimension: 'Empatía',
      whatItMeasures: 'Comprensión emocional de otros, sensibilidad social.',
      highIndicators: 'Escucha activa, tacto, comprensión genuina.',
      lowIndicators: 'Ignora señales emocionales, comunicación fría.'
    },
    {
      dimension: 'Habilidades sociales',
      whatItMeasures: 'Comunicación, influencia, resolución de conflictos.',
      highIndicators: 'Coopera, persuade y mantiene buenas relaciones.',
      lowIndicators: 'Evita conversaciones difíciles o impone ideas.'
    },
    {
      dimension: 'Manejo del estrés',
      whatItMeasures: 'Tolerancia a la presión, equilibrio emocional.',
      highIndicators: 'Prioriza, cuida su energía, pide ayuda a tiempo.',
      lowIndicators: 'Se abruma, pierde foco o se sobrecarga.'
    }
  ];
};

const PersonalHabilitiesTestResult = ({ test }) => {
  const { t, i18n } = useTranslation();
  const { results, aiAnalysis, testType } = test;
  const rawLanguage = test.language || i18n.language || 'es';
  const language = rawLanguage?.startsWith('en') ? 'en' : 'es';

  if (!results) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('personalHabilitiesTests.noResults')}
      </Typography>
    );
  }

  const {
    correctTotal = 0,
    correctResponded = 0,
    questionsAnswered = 0,
    respondedPercent = 0,
    timeReal = 0,
    timeTotal = 0,
    correct = 0,
    total = 0
  } = results;

  const confidenceIndex = test.confidenceIndex;

  const integrityDimensions = testType === 'integrityTest'
    ? getIntegrityDimensionsForReport(results, language)
    : [];

  const emotionalDimensionGuide = testType === 'emotionalIntelligence'
    ? getEmotionalIntelligenceDimensionGuide(language)
    : [];

  const integrityTexts = {
    dimension: language === 'es' ? 'Dimensión' : 'Dimension',
    score: language === 'es' ? 'Puntaje' : 'Score',
    dimensionsTitle: language === 'es' ? 'Puntajes por Dimensión' : 'Dimension Scores',
    glossaryTitle: language === 'es' ? 'Glosario de Dimensiones' : 'Dimension Glossary'
  };

  const emotionalTexts = {
    dimension: language === 'es' ? 'Dimensión' : 'Dimension',
    whatItMeasures: language === 'es' ? 'Qué mide' : 'What it measures',
    highIndicators: language === 'es' ? 'Indicadores altos' : 'High indicators',
    lowIndicators: language === 'es' ? 'Indicadores bajos' : 'Low indicators',
    guideTitle: language === 'es' ? 'Glosario de Dimensiones' : 'Dimension Glossary'
  };

  const formatTime = (ms) => {
    if (!ms || isNaN(ms)) {
      return '0:00';
    }
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const getConfidenceText = (index) => {
    if (index >= 90) return t('personalHabilitiesTests.confidenceHigh');
    if (index >= 70) return t('personalHabilitiesTests.confidenceMedium');
    if (index >= 50) return t('personalHabilitiesTests.confidenceLow');
    return t('personalHabilitiesTests.confidenceVeryLow');
  };

  const getTestTitle = () => {
    const titles = {
      'integrityTest': t('personalHabilitiesTests.integrityTest'),
      'emotionalIntelligence': t('personalHabilitiesTests.emotionalIntelligence'),
    };
    return titles[testType] || t('personalHabilitiesTests.title');
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {getTestTitle()}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('personalHabilitiesTests.resultsTitle')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {test.applicantName}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '2px solid #e0e0e0',
          borderRadius: 2
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
          {t('personalHabilitiesTests.technicalDetails')}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('personalHabilitiesTests.metric')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('personalHabilitiesTests.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testType === 'integrityTest' ? (
                <>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.integrityIGI')}</TableCell>
                    <TableCell align="right">{results.IGI || 0}/100</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.integrityRiskLevel')}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: results.riskLevel === 'low' ? 'success.main' :
                            results.riskLevel === 'medium' ? 'warning.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {results.riskLevel === 'low' ? t('personalHabilitiesTests.integrityLowRisk') :
                          results.riskLevel === 'medium' ? t('personalHabilitiesTests.integrityMediumRisk') :
                            t('personalHabilitiesTests.integrityHighRisk')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.questionsAnswered')}</TableCell>
                    <TableCell align="right">{questionsAnswered} / {total}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.answeredPercent')}</TableCell>
                    <TableCell align="right">{respondedPercent}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.integrityValidResponses')}</TableCell>
                    <TableCell align="right">
                      {results.validityCheck?.isValid ?
                        t('personalHabilitiesTests.integrityValid') :
                        t('personalHabilitiesTests.integrityQuestionable')}
                    </TableCell>
                  </TableRow>
                  {confidenceIndex !== undefined && confidenceIndex !== null && (
                    <TableRow>
                      <TableCell>{t('personalHabilitiesTests.confidenceIndex')}</TableCell>
                      <TableCell align="right">
                        {confidenceIndex.toFixed(1)}% ({getConfidenceText(confidenceIndex)})
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ) : testType === 'emotionalIntelligence' ? (
                <>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.emotionalCIE')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {results.CIE || 0}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.emotionalLevel')}</TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: results.level === 'high' ? 'success.main' :
                            results.level === 'medium' ? 'warning.main' : 'error.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {results.level === 'high' ? t('personalHabilitiesTests.emotionalHigh') :
                          results.level === 'medium' ? t('personalHabilitiesTests.emotionalMedium') :
                            t('personalHabilitiesTests.emotionalLow')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.questionsAnswered')}</TableCell>
                    <TableCell align="right">{questionsAnswered} / {total}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.answeredPercent')}</TableCell>
                    <TableCell align="right">{respondedPercent}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.realTime')}</TableCell>
                    <TableCell align="right">{formatTime(timeReal)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.totalTime')}</TableCell>
                    <TableCell align="right">{formatTime(timeTotal)}</TableCell>
                  </TableRow>
                  {confidenceIndex !== undefined && confidenceIndex !== null && (
                    <TableRow>
                      <TableCell>{t('personalHabilitiesTests.confidenceIndex')}</TableCell>
                      <TableCell align="right">
                        {confidenceIndex.toFixed(1)}% ({getConfidenceText(confidenceIndex)})
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ) : (
                <>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.correctTotal')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {Math.round((correctTotal / 100) * total)}/{total} ({correctTotal}%)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.correctResponded')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {Math.round((correctResponded / 100) * questionsAnswered)}/{questionsAnswered} ({correctResponded}%)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.questionsAnswered')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {questionsAnswered}/{total}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.answeredPercent')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {respondedPercent}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.realTime')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {formatTime(timeReal)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('personalHabilitiesTests.totalTime')}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {formatTime(timeTotal)}
                    </TableCell>
                  </TableRow>
                  {confidenceIndex !== undefined && confidenceIndex !== null && (
                    <TableRow>
                      <TableCell>{t('personalHabilitiesTests.confidenceIndex')}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {confidenceIndex.toFixed(1)}% ({getConfidenceText(confidenceIndex)})
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {testType === 'integrityTest' && integrityDimensions.length > 0 && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: '2px solid #e0e0e0',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              {integrityTexts.dimensionsTitle}
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {integrityTexts.dimension}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {integrityTexts.score}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {integrityDimensions.map((dimension) => (
                    <TableRow key={dimension.key}>
                      <TableCell>{dimension.name}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {dimension.score || 0}/100
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: '2px solid #e0e0e0',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
              {integrityTexts.glossaryTitle}
            </Typography>

            <Box>
              {integrityDimensions.map((dimension) => (
                <Box key={dimension.key} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {dimension.name}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'justify' }}>
                    {dimension.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </>
      )}

      {testType === 'emotionalIntelligence' && results.dimensionScores && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '2px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            {t('personalHabilitiesTests.emotionalDimensions')}
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {language === 'es' ? 'Dimensión' : 'Dimension'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {language === 'es' ? 'Puntaje' : 'Score'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(results.dimensionScores).map(([dimension, score]) => (
                  <TableRow key={dimension}>
                    <TableCell>{dimension}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {score}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {testType === 'emotionalIntelligence' && emotionalDimensionGuide.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '2px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            {emotionalTexts.guideTitle}
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {emotionalTexts.dimension}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {emotionalTexts.whatItMeasures}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {emotionalTexts.highIndicators}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {emotionalTexts.lowIndicators}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emotionalDimensionGuide.map((item) => (
                  <TableRow key={item.dimension}>
                    <TableCell>{item.dimension}</TableCell>
                    <TableCell>{item.whatItMeasures}</TableCell>
                    <TableCell>{item.highIndicators}</TableCell>
                    <TableCell>{item.lowIndicators}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {aiAnalysis && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '2px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
            {t('personalHabilitiesTests.performanceInterpretation')}
          </Typography>

          {(() => {
            try {
              let cleanedAnalysis = aiAnalysis;

              if (typeof aiAnalysis === 'string') {
                cleanedAnalysis = cleanedAnalysis.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

                cleanedAnalysis = cleanedAnalysis.replace(/\.\]\s*"\s*}/g, '." }');

                cleanedAnalysis = cleanedAnalysis.trim();
                if (cleanedAnalysis.endsWith(']}')) {
                  const lastBraceIndex = cleanedAnalysis.lastIndexOf('}');
                  if (lastBraceIndex !== -1) {
                    cleanedAnalysis = cleanedAnalysis.substring(0, lastBraceIndex + 1);
                  }
                } else if (cleanedAnalysis.endsWith(']')) {
                  cleanedAnalysis = cleanedAnalysis.slice(0, -1).trim();
                }

                cleanedAnalysis = cleanedAnalysis.replace(/\s+/g, ' ');
              }

              const analysis = typeof cleanedAnalysis === 'string' ?
                JSON.parse(cleanedAnalysis) : cleanedAnalysis;

              return (
                <Box>
                  {(analysis.analisisGeneral || analysis.generalAnalysis) && (
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, textAlign: 'justify' }}>
                      {analysis.analisisGeneral || analysis.generalAnalysis}
                    </Typography>
                  )}

                  {(analysis.fortalezas || analysis.strengths) && (
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, textAlign: 'justify' }}>
                      {analysis.fortalezas || analysis.strengths}
                    </Typography>
                  )}

                  {(analysis.areasDesarrollo || analysis.developmentAreas) && (
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, textAlign: 'justify' }}>
                      {analysis.areasDesarrollo || analysis.developmentAreas}
                    </Typography>
                  )}

                  {(analysis.recomendaciones || analysis.recommendations) && (
                    <Typography variant="body1" sx={{ lineHeight: 1.8, textAlign: 'justify' }}>
                      {analysis.recomendaciones || analysis.recommendations}
                    </Typography>
                  )}
                </Box>
              );
            } catch (error) {
              console.error('Error parseando aiAnalysis:', error);
              console.error('aiAnalysis original:', aiAnalysis);

              return (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                  {typeof aiAnalysis === 'string' ?
                    aiAnalysis : JSON.stringify(aiAnalysis, null, 2)}
                </Typography>
              );
            }
          })()}
        </Paper>
      )}
    </Box>
  );
};

export default PersonalHabilitiesTestResult;